/* eslint react-hooks/exhaustive-deps: "warn" */

import { useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useIntersection } from 'hooks/useIntersection';

import { DemoCard } from 'ui/molecules/DemoCard/DemoCard';

import styles from './NetworkScreeningCard.module.scss';

import type { NetworkScreeningCardProps } from './NetworkScreeningCard.types';

type Res = 'ok' | 'rev' | 'all';
type RowState = 'queued' | 'checking' | 'clear' | 'review' | 'allclear';

interface Row {
  icon: string;
  person?: boolean;
  name: string;
  meta: string;
  res: Res;
}

const ROWS: Row[] = [
  { icon: '👤', person: true, name: 'Martin Kessler', meta: 'Person · Director & PSC', res: 'ok' },
  { icon: '🇬🇧', name: 'Kessler Group Holdings Ltd', meta: 'Company · United Kingdom', res: 'ok' },
  { icon: '👤', person: true, name: 'A. Mahmud', meta: 'Shareholder · 3.1%', res: 'rev' },
  { icon: '🇩🇪', name: 'Kessler GmbH Niederlassung', meta: 'Company · Germany', res: 'ok' },
  { icon: '＋', name: '30 more companies & individuals', meta: 'Screened in the same pass', res: 'all' },
];

const TOTAL = 34;
const STEP = 520;
const RESOLVE = 700;

const finalState = (res: Res): RowState => (res === 'rev' ? 'review' : res === 'all' ? 'allclear' : 'clear');

const BADGE_LABEL: Record<RowState, string> = {
  queued: 'Queued',
  checking: 'Checking…',
  clear: 'Clear',
  review: 'PEP · Review',
  allclear: 'All clear',
};

export const NetworkScreeningCard = ({ className }: NetworkScreeningCardProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useIntersection(ref, { continuous: true, threshold: 0.45 });
  const timers = useRef<number[]>([]);
  const rafRef = useRef<number>(undefined);
  const started = useRef(false);

  const [reduced, setReduced] = useState(false);
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);
  const [screened, setScreened] = useState(0);
  const [progress, setProgress] = useState(0);
  const [rowStates, setRowStates] = useState<RowState[]>(ROWS.map(() => 'queued'));

  useEffect(() => {
    setReduced(window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }, []);

  const clearTimers = () => {
    timers.current.forEach((t) => window.clearTimeout(t));
    timers.current = [];
    if (rafRef.current) {
      window.cancelAnimationFrame(rafRef.current);
    }
  };

  const jumpToFinal = () => {
    clearTimers();
    setRunning(false);
    setDone(true);
    setScreened(TOTAL);
    setProgress(100);
    setRowStates(ROWS.map((r) => finalState(r.res)));
  };

  const run = () => {
    clearTimers();
    setRunning(true);
    setDone(false);
    setScreened(0);
    setProgress(0);
    setRowStates(ROWS.map(() => 'queued'));

    const total = ROWS.length * STEP + RESOLVE + 300;
    const t0 = performance.now();

    const tick = () => {
      const k = Math.min(1, (performance.now() - t0) / (total - 300));
      setScreened(Math.round(TOTAL * k));
      setProgress(k * 100);
      if (k < 1) {
        rafRef.current = window.requestAnimationFrame(tick);
      }
    };
    rafRef.current = window.requestAnimationFrame(tick);

    ROWS.forEach((row, i) => {
      timers.current.push(
        window.setTimeout(() => {
          setRowStates((prev) => prev.map((s, j) => (j === i ? 'checking' : s)));
        }, i * STEP),
      );
      timers.current.push(
        window.setTimeout(
          () => {
            setRowStates((prev) => prev.map((s, j) => (j === i ? finalState(row.res) : s)));
          },
          i * STEP + RESOLVE,
        ),
      );
    });

    timers.current.push(
      window.setTimeout(() => {
        setRunning(false);
        setDone(true);
        setScreened(TOTAL);
        setProgress(100);
      }, total),
    );
  };

  // Autoplay once when it scrolls into view; reduced-motion jumps straight to final.
  useEffect(() => {
    if (!inView || started.current) {
      return;
    }
    started.current = true;
    if (reduced) {
      jumpToFinal();
    } else run();
    // run once when it enters view (guarded by started.current); run/jumpToFinal intentionally omitted
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView, reduced]);

  useEffect(() => () => clearTimers(), []);

  const flagged = done || rowStates.includes('review');
  const cleared = done || rowStates.some((s, i) => ROWS[i].res === 'ok' && s === 'clear');

  return (
    <div ref={ref} className={cn(styles.wrap, className)}>
      <DemoCard
        title="Network screening"
        status="OFAC · EU · UN · UK HMT"
        live={false}
        footerLeft={
          done
            ? 'Flag found on a 3.1% shareholder, two layers below the target — the kind manual checks miss.'
            : undefined
        }
        bodyClassName={styles.body}
      >
        <div className={styles.sum}>
          <span className={styles.chip}>
            <b>{screened}</b> screened
          </span>
          <span className={cn(styles.chip, cleared && styles.chipOk)}>
            <b>0</b> sanctions
          </span>
          <span className={cn(styles.chip, flagged && styles.chipRev)}>
            <b>{flagged ? 1 : 0}</b> PEP{flagged ? ' · review' : ''}
          </span>

          <button type="button" className={styles.run} onClick={run} disabled={running}>
            {running ? 'Screening…' : done ? '↻ Run again' : '▶ Run screening'}
          </button>
        </div>

        <div className={styles.prog} aria-hidden="true">
          <i style={{ width: `${progress}%` }} />
        </div>

        <div className={styles.rows}>
          {ROWS.map((row, i) => {
            const state = rowStates[i];

            return (
              <div key={row.name} className={cn(styles.row, state === 'review' && styles.rowFlag)}>
                <span className={cn(styles.ic, row.person && styles.icPerson)}>{row.icon}</span>
                <span className={styles.name}>
                  {row.name}
                  <small>{row.meta}</small>
                </span>
                <span
                  className={cn(
                    styles.badge,
                    state === 'checking' && styles.badgeChecking,
                    state === 'review' && styles.badgeRev,
                    (state === 'clear' || state === 'allclear') && styles.badgeOk,
                  )}
                >
                  {BADGE_LABEL[state]}
                </span>
              </div>
            );
          })}
        </div>
      </DemoCard>
    </div>
  );
};
